<template>
    <team-user-details :teamId="props.teamId" :userId="props.userId">
        <div class="col-span-12 lg:col-span-10">
            <h2 class="text-2xl font-extrabold mb-6">Targets</h2>

            <vx-alert color="slate" shadow class="mb-6" :closeable="false">
                <template v-slot:icon>
                    <font-awesome-icon :icon="faFingerprint" />
                </template>

                <div class="flex gap-3 py-2">
                    <div class="shrink-0">
                        <strong>Access Summary</strong>
                    </div>
                    <div v-if="editingUserIsOwnerOrAdmin">
                        This user is a team
                        <strong>
                            {{ TEAM_USER_ROLES[user.role] || '-' }}
                        </strong>
                        and has
                        <font-awesome-icon :icon="faCrown" class="text-amber-400 mr-0.5" />
                        <strong>Corporate</strong> access and access to
                        <font-awesome-icon :icon="faBuildingFlag" class="text-violet-500 mr-0.5" />
                        <strong>all OUs</strong> and
                        <font-awesome-icon :icon="faGrid2Plus" class="text-slate-500 mr-0.5" />
                        <strong>all Groups</strong>.
                    </div>
                    <div v-else data-test="access-summary">
                        This user is in the team role
                        <strong>
                            {{ TEAM_USER_ROLES[user.role] || '-' }}
                        </strong>
                        and has
                        <template v-if="user.role !== 'none'">
                            <font-awesome-icon :icon="faCrown" class="text-amber-400 mr-0.5" />
                            <strong>Corporate</strong> access,
                        </template>
                        <font-awesome-icon :icon="faBuildingFlag" class="text-violet-500 mr-0.5" />
                        <strong>{{ user.org_unit_count }} {{ user.org_unit_count === 1 ? 'OU' : 'OUs' }}</strong>
                        and
                        <font-awesome-icon :icon="faGrid2Plus" class="text-slate-500 mr-0.5" />
                        <strong>{{ user.group_count }} {{ user.group_count === 1 ? 'Group' : 'Groups' }}</strong>
                        for this team.
                    </div>
                </div>
            </vx-alert>

            <page-controls v-if="!editingUserIsOwnerOrAdmin">
                <template v-slot:left>
                    <div class="grow @3xl/controls:max-w-md">
                        <vx-filterable :active="filterActive" @toggle="filterActive = $event" data-test="filters">
                            <template v-slot:placeholder>
                                <span class="mr-2">Find</span>
                                <vx-badge
                                    v-if="filtersCount > 0"
                                    data-test="reset-filters"
                                    :borders="false"
                                    size="xs"
                                    @click.stop="resetFilters"
                                >
                                    {{ pluralized('filter', filtersCount, true) }}
                                    <font-awesome-icon widthAuto :icon="faCircleXmark" />
                                </vx-badge>
                            </template>
                            <template v-slot:form>
                                <form @submit.prevent="applyFilters" class="flex flex-col gap-4">
                                    <div>
                                        <filter-label :canReset="!!filters.query" @reset="filters.query = undefined">
                                            Keyword Search
                                        </filter-label>
                                        <vx-input
                                            type="text"
                                            data-test="filters-query"
                                            placeholder="Search"
                                            maxlength="191"
                                            size="lg"
                                            v-model="filters.query"
                                        >
                                            <template v-slot:right>
                                                <font-awesome-icon
                                                    widthAuto
                                                    class="text-xs"
                                                    :icon="faMagnifyingGlass"
                                                />
                                            </template>
                                        </vx-input>
                                    </div>

                                    <div class="flex gap-2 pt-4">
                                        <vx-button
                                            data-test="cancel-filters"
                                            type="button"
                                            color="muted-light"
                                            size="lg"
                                            @click="cancelFilters"
                                        >
                                            Cancel
                                        </vx-button>
                                        <vx-button data-test="filters-apply" type="submit" size="lg" block>
                                            Apply
                                            <font-awesome-icon widthAuto :icon="faCircleCheck" />
                                        </vx-button>
                                    </div>
                                </form>
                            </template>
                        </vx-filterable>
                    </div>
                    <div class="flex w-full gap-2 @xl/controls:w-auto">
                        <vx-button-group size="lg" class="w-full">
                            <vx-select
                                :modelValue="fetchParams.sort_field"
                                @update:modelValue="
                                    fetchParams.sort_field = String($event);
                                    loadData();
                                "
                                :options="sortOptions"
                            />
                            <vx-button
                                @click.prevent="
                                    fetchParams.sort_dir = fetchParams.sort_dir === 'asc' ? 'desc' : 'asc';
                                    loadData();
                                "
                                type="button"
                                color="white"
                                shadow
                            >
                                <font-awesome-icon
                                    widthAuto
                                    :icon="fetchParams.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                                />
                            </vx-button>
                        </vx-button-group>
                    </div>
                </template>

                <template v-if="isAdminOrOwner" v-slot:right>
                    <div class="flex gap-2">
                        <vx-button
                            size="lg"
                            data-test="add-to-ou"
                            color="violet"
                            @click="
                                addingOu = true;
                                addingGroup = false;
                            "
                        >
                            Add to OU
                            <font-awesome-icon widthAuto :icon="faBuildingFlag" />
                        </vx-button>
                        <vx-button
                            size="lg"
                            data-test="add-to-group"
                            color="muted-dark"
                            @click="
                                addingGroup = true;
                                addingOu = false;
                            "
                        >
                            Add to Group
                            <font-awesome-icon widthAuto :icon="faGrid2Plus" />
                        </vx-button>
                    </div>
                </template>
            </page-controls>

            <page-loading v-if="loadingTargets && !combinedList.length" />

            <template
                v-if="
                    !editingUserIsOwnerOrAdmin && !loadingTargets && !combinedList.length && !addingOu && !addingGroup
                "
            >
                <vx-box
                    v-if="filtersCount > 0"
                    color="info"
                    class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500"
                >
                    No results found.
                    <vx-button @click="resetFilters" data-test="empty-state-reset"> Reset Filters </vx-button>
                </vx-box>
                <vx-box v-else color="info" class="flex flex-col px-8 py-20 gap-6 items-center text-xl text-slate-500">
                    No target access configured.
                </vx-box>
            </template>

            <vx-table
                v-if="!editingUserIsOwnerOrAdmin && (combinedList.length > 0 || addingOu || addingGroup)"
                class="xl:grid-cols-[1fr_auto_auto_auto]"
                rowBreakpoint="xl"
            >
                <vx-table-row isHeader>
                    <div>Name</div>
                    <div>Type</div>
                    <div>Role</div>
                </vx-table-row>

                <target-access-add-row
                    v-if="addingOu"
                    type="ou"
                    :teamId="teamId"
                    :userId="props.userId"
                    @saved="onTargetAdded"
                    @cancel="addingOu = false"
                    data-test="add-ou-row"
                />

                <target-access-add-row
                    v-if="addingGroup"
                    type="group"
                    :teamId="teamId"
                    :userId="props.userId"
                    @saved="onTargetAdded"
                    @cancel="addingGroup = false"
                    data-test="add-group-row"
                />

                <target-access-row
                    v-for="target in combinedList"
                    :key="`${target.type}-${target.targetId}`"
                    :teamId="teamId"
                    :userId="props.userId"
                    :target="target"
                    :editable="isAdminOrOwner"
                    @removed="onTargetRemoved"
                    @roleUpdated="onRoleUpdated"
                />
            </vx-table>

            <div
                v-if="!editingUserIsOwnerOrAdmin && combinedList.length"
                class="mt-8 flex justify-between w-full gap-10"
            >
                <vx-button
                    v-if="hasMore"
                    @click="loadMore()"
                    color="muted-dark"
                    size="xl"
                    data-test="load-more-btn"
                    class="grow-0"
                    :loading="loadingTargets"
                >
                    Load More
                    <font-awesome-icon widthAuto :icon="faSpinner" />
                </vx-button>
                <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                    {{ combinedList.length }} / {{ user.org_unit_count + user.group_count }}
                </div>
            </div>
        </div>
    </team-user-details>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import {
    VxAlert,
    VxBadge,
    VxBox,
    VxButton,
    VxButtonGroup,
    VxFilterable,
    VxInput,
    VxSelect,
    VxTable,
    VxTableRow,
} from '@voxie/frontend-components';
import {
    faArrowDownWideShort,
    faArrowUpWideShort,
    faBuildingFlag,
    faCircleCheck,
    faCircleXmark,
    faCrown,
    faFingerprint,
    faGrid2Plus,
    faMagnifyingGlass,
    faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TeamUserDetails from './TeamUserDetails.vue';
import PageControls from '~/components/general/PageControls.vue';
import PageLoading from '~/components/general/PageLoading.vue';
import FilterLabel from '~/components/general/FilterLabel.vue';
import TargetAccessRow from './TargetAccessRow.vue';
import TargetAccessAddRow from './TargetAccessAddRow.vue';
import { useTeamUserDetails } from './useTeamUserDetails';
import usersClient from '~/services/usersClient';
import { orgUnitUsersClient, groupUsersClient } from '~/services';
import { useToasted } from '~/composables/useToasted';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { catchRequestError } from '~/utils/request';
import type { CursorPaginationParams } from '~/services/BaseClient.types';
import { TEAM_USER_ROLES, ORG_UNIT_USER_ROLES, GROUP_USER_ROLES } from '~/constants/roles';
import { pluralized } from '~/components/filters';

type TargetType = 'ou' | 'group';

interface TargetAccess {
    id?: number;
    group_id?: string;
    name: string;
    role: string;
}

interface Target {
    type: TargetType;
    targetId: string | number;
    name: string;
    role: string;
}

const isAdminOrOwner = Spark.isAdminOrOwner;
const toasted = useToasted();

const props = withDefaults(
    defineProps<{
        teamId?: number;
        userId: number;
    }>(),
    {
        teamId: () => Spark.state.currentTeam.id,
    }
);

const { user, onMountedUser } = useTeamUserDetails();

// Two independent cursor-paginated sources merged into one list
const {
    list: ouList,
    loading: ouLoading,
    fetchParams: ouFetchParams,
    fetchRecords: fetchOuRecords,
    isLast: ouIsLast,
} = useCursorPagination<TargetAccess>();

const {
    list: groupList,
    loading: groupLoading,
    fetchParams: groupFetchParams,
    fetchRecords: fetchGroupRecords,
    isLast: groupIsLast,
} = useCursorPagination<TargetAccess>();

const getTargetList = (type: TargetType) => (type === 'ou' ? ouList : groupList);
const getTargetIdField = (type: TargetType): keyof TargetAccess => (type === 'ou' ? 'id' : 'group_id');

const upsertTargetUser = (type: TargetType, targetId: string | number, data: { role: string }) =>
    type === 'ou'
        ? orgUnitUsersClient.upsertOrgUnitUser(props.teamId, targetId, props.userId, data)
        : groupUsersClient.upsertGroupUser(props.teamId, targetId, props.userId, data);

const removeTargetUser = (type: TargetType, targetId: string | number) =>
    type === 'ou'
        ? orgUnitUsersClient.deleteOrgUnitUser(props.teamId, targetId, props.userId)
        : groupUsersClient.deleteGroupUser(props.teamId, targetId, props.userId);

const editingUserIsOwnerOrAdmin = computed(() => ['owner', 'admin'].includes(user.value.role));
const loadingTargets = computed(() => ouLoading.value || groupLoading.value);
const hasMore = computed(() => !ouIsLast.value || !groupIsLast.value);

const recentlyAdded = ref<Target[]>([]);
const addingOu = ref(false);
const addingGroup = ref(false);
const filterActive = ref(false);

const filters = ref<CursorPaginationParams>({
    sort_dir: 'asc',
    sort_field: 'type',
});

const fetchParams = ref<CursorPaginationParams>({
    sort_dir: 'asc',
    sort_field: 'type',
});

watch(
    () => fetchParams.value,
    (value) => {
        filters.value = { ...value };
    },
    { deep: true, immediate: true }
);

const sortOptions = [
    { label: 'Sort: Type', value: 'type' },
    { label: 'Sort: Name', value: 'name' },
];

const filtersCount = computed(() => (fetchParams.value.query ? 1 : 0));

const recentKeys = computed(() => new Set(recentlyAdded.value.map((t) => `${t.type}-${t.targetId}`)));

// Merge both paginated sources and sort client-side
const combinedList = computed((): Target[] => {
    const all: Target[] = [
        ...ouList.value.map((ou): Target => ({ name: ou.name, type: 'ou', role: ou.role, targetId: ou.id! })),
        ...groupList.value.map((g): Target => ({ name: g.name, type: 'group', role: g.role, targetId: g.group_id! })),
    ];

    // Recently added targets are pinned to the top
    const pinned = all.filter((t) => recentKeys.value.has(`${t.type}-${t.targetId}`));
    const rest = all.filter((t) => !recentKeys.value.has(`${t.type}-${t.targetId}`));

    const field = fetchParams.value.sort_field || 'type';
    const dir = fetchParams.value.sort_dir || 'asc';
    rest.sort((a, b) => {
        let cmp: number;
        if (field === 'type') {
            // OUs first (asc), then groups; secondary sort by name
            const typeOrder: Record<TargetType, number> = { ou: 0, group: 1 };
            cmp = typeOrder[a.type] - typeOrder[b.type] || a.name.localeCompare(b.name);
        } else {
            cmp = a.name.localeCompare(b.name);
        }
        return dir === 'asc' ? cmp : -cmp;
    });

    return [...pinned, ...rest];
});

const loadOUs = () => {
    ouFetchParams.value = {
        ...ouFetchParams.value,
        query: fetchParams.value.query || undefined,
        sort_dir: 'asc',
        per_page: 100,
    };
    fetchOuRecords(usersClient.getUserOrgUnits(props.teamId, props.userId, ouFetchParams.value));
};

const loadGroups = () => {
    groupFetchParams.value = {
        ...groupFetchParams.value,
        query: fetchParams.value.query || undefined,
        sort_dir: 'asc',
        per_page: 100,
    };
    fetchGroupRecords(usersClient.getUserGroups(props.teamId, props.userId, groupFetchParams.value));
};

// Reset cursors and fetch first page of both sources
const loadData = () => {
    recentlyAdded.value = [];
    ouFetchParams.value = { cursor: null };
    groupFetchParams.value = { cursor: null };
    loadOUs();
    loadGroups();
};

const loadMore = () => {
    if (!ouIsLast.value) {
        loadOUs();
    }
    if (!groupIsLast.value) {
        loadGroups();
    }
};

const applyFilters = async () => {
    filterActive.value = false;
    fetchParams.value = { ...filters.value };
    await nextTick();
    loadData();
};

const cancelFilters = () => {
    filters.value = { ...fetchParams.value };
    filterActive.value = false;
};

const resetFilters = () => {
    filterActive.value = false;
    fetchParams.value = {
        sort_dir: 'asc',
        sort_field: 'type',
    };
    loadData();
};

const onTargetAdded = (target: Target) => {
    const list = getTargetList(target.type);
    const idField = getTargetIdField(target.type);
    const existingItem = list.value.find((i) => i[idField] === target.targetId);
    const previousRole = existingItem?.role;
    const countField = target.type === 'ou' ? 'org_unit_count' : 'group_count';

    if (existingItem) {
        existingItem.role = target.role;
    } else {
        list.value = [{ [idField]: target.targetId, name: target.name, role: target.role }, ...list.value];
        user.value[countField]++;
    }

    recentlyAdded.value = [target, ...recentlyAdded.value];

    if (target.type === 'ou') {
        addingOu.value = false;
    } else {
        addingGroup.value = false;
    }

    const rolesMap = target.type === 'ou' ? ORG_UNIT_USER_ROLES : GROUP_USER_ROLES;
    const toastId = toasted.show({
        title: existingItem ? `${target.name} role updated to ${rolesMap[target.role]}` : `Added to ${target.name}`,
        type: 'success',
        action: {
            label: 'Undo',
            onClick: async () => {
                toasted.dismiss(toastId);
                try {
                    if (existingItem) {
                        await upsertTargetUser(target.type, target.targetId, { role: previousRole! });
                        onRoleUpdated({ ...target, role: previousRole! });
                    } else {
                        await removeTargetUser(target.type, target.targetId);
                        onTargetRemoved(target);
                        recentlyAdded.value = recentlyAdded.value.filter(
                            (r) => !(r.type === target.type && r.targetId === target.targetId)
                        );
                    }
                } catch (e) {
                    toasted.error(catchRequestError(e));
                }
            },
        },
    });
};

const onTargetRemoved = (target: Target) => {
    const list = getTargetList(target.type);
    const idField = getTargetIdField(target.type);
    list.value = list.value.filter((item) => item[idField] !== target.targetId);
    user.value[target.type === 'ou' ? 'org_unit_count' : 'group_count']--;
};

const onRoleUpdated = (target: Target) => {
    const list = getTargetList(target.type);
    const idField = getTargetIdField(target.type);
    const item = list.value.find((i) => i[idField] === target.targetId);
    if (item) {
        item.role = target.role;
    }
};

onMountedUser(() => {
    loadData();
});
</script>
